import type React from "react";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { formatBytes } from "@/lib/format";
import type { ExportAuditEntry } from "@/lib/api/organizations";

const FORMATS: Record<string, string> = { csv: "CSV", parquet: "Parquet" };

/// A file the viewer fetched to show. The list folds these away.
export const isPreview = (entry: ExportAuditEntry): boolean =>
  entry.door === "attachment" && entry.purpose === "preview";

/// What the person did, to what, and how, by the door and the client's label.
export function exportPhrase(entry: ExportAuditEntry): {
  verb: string;
  /// The name set in ink: the table, or the file for an attachment.
  object: string;
  /// Follows the object.
  how?: string;
  Mark: React.ElementType;
} {
  const table = entry.table_name || entry.table_id;
  if (entry.door === "attachment") {
    const [verb, Mark] = isPreview(entry)
      ? ["previewed", VisibilityOutlinedIcon]
      : ["downloaded", AttachFileOutlinedIcon];
    const file = entry.detail["filename"];
    return {
      verb,
      object: typeof file === "string" ? file : "a file",
      how: `from ${table}`,
      Mark,
    };
  }
  if (entry.door === "sheets")
    return {
      verb: "exported",
      object: table,
      how: "to Google Sheets",
      Mark: GridOnOutlinedIcon,
    };
  if (entry.purpose === "clipboard")
    return { verb: "copied", object: table, Mark: ContentCopyOutlinedIcon };
  if (entry.door === "stream")
    return {
      verb: "pulled",
      object: table,
      how: "with a token",
      Mark: TerminalOutlinedIcon,
    };
  return { verb: "exported", object: table, Mark: FileDownloadOutlinedIcon };
}

/// "12,400 rows · 3.1 MB · CSV", leaving out what the row does not know.
export function exportFigures(entry: ExportAuditEntry): string {
  const rows =
    entry.row_count === null
      ? null
      : `${entry.row_count.toLocaleString()} ${entry.row_count === 1 ? "row" : "rows"}`;
  const format = entry.purpose ? FORMATS[entry.purpose] : undefined;
  return [rows, formatBytes(entry.byte_count), format]
    .filter(Boolean)
    .join(" · ");
}
